/**
 * Pure OverlaySpec → MapLibre source/layer builders for the M9 demographics /
 * census choropleth, added to the M4 GPU render path. Like mapLayers.ts and
 * analysisLayers.ts this has NO maplibre-gl import — everything is plain GeoJSON
 * + JSON paint expressions built from token-KEYED ramp stops resolved via
 * `MapTokens`, so the renderer stays a thin effect layer and these stay
 * unit-testable. Because the output is a pure function of
 * `{boundaries, values, choropleth, tokens, opacity}` (never `mode`), editor and
 * the M6 read-only embed produce the SAME choropleth for the same inputs (the
 * one-spec / embed-fidelity contract). NO raw hex — every class color comes from
 * `resolveStopColor` against the compiled token artifact.
 *
 * The choropleth fill sits at the very BOTTOM of the layer stack (a base
 * thematic layer beneath every analysis overlay and marker).
 */

import { resolveStopColor } from "@/lib/overlay/ramp";
import type { Boundary, Choropleth } from "@/lib/overlay/types";
import type { MapExpr, MapTokens } from "@/lib/map";

export const OVERLAY_BOUNDARY_SOURCE = "overlay-boundaries";
export const LAYER_OVERLAY_FILL = "overlay-choropleth-fill";
export const LAYER_OVERLAY_LINE = "overlay-choropleth-line";

/** Choropleth boundary-outline width + opacity (unitless floats, not tokens). */
const OVERLAY_LINE_WIDTH = 0.5;
const OVERLAY_LINE_OPACITY = 0.4;

interface MultiPolygonFC {
  type: "FeatureCollection";
  features: Array<{
    type: "Feature";
    geometry: { type: "MultiPolygon"; coordinates: [number, number][][][] };
    properties: Record<string, string | number>;
  }>;
}

const emptyFC = (): MultiPolygonFC => ({ type: "FeatureCollection", features: [] });

/** A boundary→value map as either a Map or a plain record (snapshot-friendly). */
export type ValueByBoundaryId = Map<string, number> | Record<string, number>;

function valueFor(values: ValueByBoundaryId, id: string): number | undefined {
  if (values instanceof Map) return values.get(id);
  return values[id];
}

/**
 * Boundary polygons → a MultiPolygon FeatureCollection carrying each boundary's
 * numeric value under `v` (the choropleth input). Only boundaries WITH a value
 * are emitted, so every rendered polygon is shaded; boundaries without data are
 * simply absent. The step-fill expression reads `v`.
 */
export function overlayBoundaryFeatures(
  boundaries: Boundary[],
  values: ValueByBoundaryId,
): MultiPolygonFC {
  const fc = emptyFC();
  for (const b of boundaries) {
    const v = valueFor(values, b.id);
    if (v === undefined || !Number.isFinite(v)) continue;
    if (b.geometry.length === 0) continue;
    fc.features.push({
      type: "Feature",
      geometry: { type: "MultiPolygon", coordinates: b.geometry.map((ring) => [ring]) },
      properties: { _id: b.id, v, ...(b.name !== undefined ? { name: b.name } : {}) },
    });
  }
  return fc;
}

/**
 * The choropleth fill paint: a `step` over the boundary value `v`, one
 * token-resolved color per class, at the spec's opacity. When there are <2
 * classes it collapses to a flat class color; when there's no choropleth it
 * paints nothing (opacity 0) so the empty layer is inert.
 */
export function overlayFillPaint(
  choropleth: Choropleth | null,
  tokens: MapTokens,
  opacity: number,
): Record<string, MapExpr> {
  if (!choropleth || choropleth.stops.length === 0) {
    return { "fill-color": tokens.tile.boundary, "fill-opacity": 0 };
  }
  const stops = choropleth.stops;
  if (stops.length === 1) {
    return { "fill-color": resolveStopColor(stops[0]!.tokenId, tokens), "fill-opacity": opacity };
  }
  const input: MapExpr = ["to-number", ["get", "v"], 0];
  const expr: MapExpr[] = ["step", input, resolveStopColor(stops[0]!.tokenId, tokens)];
  for (let i = 1; i < stops.length; i++) {
    expr.push(stops[i]!.value, resolveStopColor(stops[i]!.tokenId, tokens));
  }
  return { "fill-color": expr, "fill-opacity": opacity };
}

/** Subtle choropleth boundary outline (map boundary token — no raw hex). */
export function overlayLinePaint(tokens: MapTokens): Record<string, MapExpr> {
  return {
    "line-color": tokens.tile.boundary,
    "line-width": OVERLAY_LINE_WIDTH,
    "line-opacity": OVERLAY_LINE_OPACITY,
  };
}

export interface OverlayLegendRow {
  /** Lower edge of the class (inclusive). */
  from: number;
  /** Upper edge of the class (exclusive), or null for the open top class. */
  to: number | null;
  /** Token-resolved class color. */
  color: string;
}

/**
 * Legend rows for the choropleth (M9-B legend component reads this): one row per
 * class with its value range + token-resolved color. Pure; token-keyed.
 */
export function overlayLegendRows(choropleth: Choropleth, tokens: MapTokens): OverlayLegendRow[] {
  return choropleth.stops.map((stop, i) => ({
    from: stop.value,
    to: choropleth.breaks[i + 1] ?? null,
    color: resolveStopColor(stop.tokenId, tokens),
  }));
}
